import React from 'react';
import type { Lead } from '../../../../types';
import {
  getMentionQueryAtCaret,
  getMentionSpanFromEventTarget,
  getMentionTokenAnchorRect,
  insertMentionAtCaret,
  replaceMentionSpan,
  type MentionMember,
} from '../../../../utils/notesMentions';
import type { NoteHistoryEntry } from '../../../../pages/LeadDetailPage';
import { noteHtmlHasContent, tryConvertMarkdownListAtCaret, tryExitEmptyListItem, tryUnwrapListOnBackspace } from './notesEditorHelpers';

const MAX_PINNED_NOTES = 5;

/** Notizen eines Leads: Verlauf, Speichern, Anpinnen, Mention-/Link-/Bild-Editor-Status. */
export function useLeadNotes(
  lead: Lead | null,
  opts: {
    reloadLead: () => Promise<void>;
    onSaved?: () => void;
    /** Geteilter „Speichert…“-Status mit den übrigen Lead-Aktionen (Kategorie, Kontakt, Baustelle, …). */
    isSaving: boolean;
    setIsSaving: React.Dispatch<React.SetStateAction<boolean>>;
  },
) {
  const { reloadLead, onSaved, isSaving, setIsSaving } = opts;

  const [noteDraft, setNoteDraft] = React.useState('');
  const [notesSaved, setNotesSaved] = React.useState(false);
  const [noteHistory, setNoteHistory] = React.useState<NoteHistoryEntry[]>([]);
  const [noteHistoryMenuEntryId, setNoteHistoryMenuEntryId] = React.useState<string | number | null>(null);

  const notesEditorRef = React.useRef<HTMLDivElement>(null);
  const linkSavedRangeRef = React.useRef<Range | null>(null);
  const notesSavedRangeRef = React.useRef<Range | null>(null);
  const notesImageInputRef = React.useRef<HTMLInputElement>(null);
  const [showLinkModal, setShowLinkModal] = React.useState(false);
  const [linkModalText, setLinkModalText] = React.useState('');
  const [linkModalUrl, setLinkModalUrl] = React.useState('');
  const [showLinkHint, setShowLinkHint] = React.useState(false);
  const imageSavedRangeRef = React.useRef<Range | null>(null);
  const [mentionPickerOpen, setMentionPickerOpen] = React.useState(false);
  const [mentionQuery, setMentionQuery] = React.useState('');
  const [mentionAnchorRect, setMentionAnchorRect] = React.useState<DOMRect | null>(null);
  const editingMentionSpanRef = React.useRef<HTMLElement | null>(null);
  const mentionAnchorRectRef = React.useRef<DOMRect | null>(null);

  const loadNoteHistory = React.useCallback(() => {
    if (!lead) return;
    fetch(`/api/leads/${lead.id}/note-history`)
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => setNoteHistory(Array.isArray(list) ? list : []))
      .catch(() => setNoteHistory([]));
  }, [lead?.id]);

  React.useEffect(() => {
    if (lead?.id) loadNoteHistory();
  }, [lead?.id, loadNoteHistory]);

  React.useEffect(() => {
    if (noteHistoryMenuEntryId == null) return;
    const onDown = (ev: MouseEvent) => {
      const t = ev.target;
      if (!(t instanceof Element)) return;
      if (!t.closest('[data-note-history-menu-root]')) setNoteHistoryMenuEntryId(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [noteHistoryMenuEntryId]);

  React.useEffect(() => {
    if (!showLinkHint) return;
    const t = setTimeout(() => setShowLinkHint(false), 3000);
    return () => clearTimeout(t);
  }, [showLinkHint]);

  React.useEffect(() => {
    if (notesEditorRef.current != null) notesEditorRef.current.innerHTML = noteDraft || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id]);

  const pinnedNotesForLead = React.useMemo(() => {
    const ids = lead?.pinned_note_history_ids;
    if (!ids?.length) return [];
    return ids
      .map((pid) => noteHistory.find((n) => String(n.id) === String(pid)))
      .filter((n): n is NoteHistoryEntry => Boolean(n));
  }, [lead?.pinned_note_history_ids, noteHistory]);

  const togglePinnedNoteHistory = React.useCallback(
    async (noteHistoryId: string | number) => {
      if (!lead) return;
      const sid = String(noteHistoryId);
      const current = (lead.pinned_note_history_ids ?? []).map(String);
      let next: string[];
      if (current.includes(sid)) {
        next = current.filter((x) => x !== sid);
      } else {
        if (current.length >= MAX_PINNED_NOTES) {
          window.alert('Es können maximal fünf Notizen angepinnt werden.');
          return;
        }
        next = [...current, sid];
      }
      try {
        const res = await fetch(`/api/leads/${lead.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pinned_note_history_ids: next }),
        });
        if (!res.ok) return;
        await reloadLead();
      } catch (e) {
        console.error(e);
      }
    },
    [lead, reloadLead],
  );

  const deleteNoteHistoryEntry = React.useCallback(
    async (entryId: string | number) => {
      if (!lead) return;
      if (!window.confirm('Diese Notiz aus dem Verlauf löschen?')) return;
      try {
        const res = await fetch(
          `/api/leads/${lead.id}/note-history/${encodeURIComponent(String(entryId))}`,
          { method: 'DELETE' },
        );
        if (!res.ok) {
          const t = await res.text();
          console.error('DELETE note-history', res.status, t);
          return;
        }
        setNoteHistoryMenuEntryId(null);
        loadNoteHistory();
        await reloadLead();
      } catch (e) {
        console.error(e);
      }
    },
    [lead, loadNoteHistory, reloadLead],
  );

  const updateMentionPickerFromEditor = React.useCallback(() => {
    const editor = notesEditorRef.current;
    if (!editor) return;
    const query = getMentionQueryAtCaret(editor);
    if (query === null) {
      setMentionPickerOpen(false);
      return;
    }
    editingMentionSpanRef.current = null;
    const rect =
      getMentionTokenAnchorRect(editor) ??
      mentionAnchorRectRef.current ??
      editor.getBoundingClientRect();
    mentionAnchorRectRef.current = rect;
    setMentionAnchorRect(rect);
    setMentionQuery(query);
    setMentionPickerOpen(true);
  }, []);

  const handleNotesEditorInput = React.useCallback(() => {
    setNoteDraft(notesEditorRef.current?.innerHTML ?? '');
    updateMentionPickerFromEditor();
  }, [updateMentionPickerFromEditor]);

  const openMentionPickerAtCaret = React.useCallback(() => {
    const editor = notesEditorRef.current;
    if (!editor) return;
    editingMentionSpanRef.current = null;
    editor.focus();
    document.execCommand('insertText', false, '@');
    setNoteDraft(editor.innerHTML ?? '');
    updateMentionPickerFromEditor();
  }, [updateMentionPickerFromEditor]);

  const closeMentionPicker = React.useCallback(() => {
    setMentionPickerOpen(false);
    setMentionQuery('');
    editingMentionSpanRef.current = null;
    mentionAnchorRectRef.current = null;
    setMentionAnchorRect(null);
  }, []);

  const handleMentionSelect = React.useCallback(
    (member: MentionMember) => {
      const editor = notesEditorRef.current;
      if (!editor) return;
      const existing = editingMentionSpanRef.current;
      if (existing && editor.contains(existing)) {
        replaceMentionSpan(existing, member);
      } else {
        insertMentionAtCaret(editor, member);
      }
      setNoteDraft(editor.innerHTML ?? '');
      closeMentionPicker();
    },
    [closeMentionPicker],
  );

  const handleNotesEditorMentionClick = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const editor = notesEditorRef.current;
    if (!editor) return;
    const span = getMentionSpanFromEventTarget(e.target, editor);
    if (!span) return;
    e.preventDefault();
    editingMentionSpanRef.current = span;
    const rect = span.getBoundingClientRect();
    mentionAnchorRectRef.current = rect;
    setMentionAnchorRect(rect);
    setMentionQuery('');
    setMentionPickerOpen(true);
  }, []);

  const handleNotesEditorKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (mentionPickerOpen) {
        if (e.key === 'Enter' || e.key === 'Tab' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
        return;
      }
      if (e.nativeEvent.isComposing) return;
      const editor = notesEditorRef.current;
      if (!editor) return;
      if (e.key === ' ' && tryConvertMarkdownListAtCaret(editor)) {
        e.preventDefault();
        setNoteDraft(editor.innerHTML ?? '');
        return;
      }
      if (e.key === 'Enter' && tryExitEmptyListItem(editor)) {
        e.preventDefault();
        setNoteDraft(editor.innerHTML ?? '');
        return;
      }
      if (e.key === 'Backspace' && tryUnwrapListOnBackspace(editor)) {
        e.preventDefault();
        setNoteDraft(editor.innerHTML ?? '');
      }
    },
    [mentionPickerOpen],
  );

  const saveNotes = React.useCallback(
    async (content?: string) => {
      if (!lead) return;
      const value = content ?? noteDraft;
      if (!noteHtmlHasContent(value)) return;
      setIsSaving(true);
      setNotesSaved(false);
      try {
        const res = await fetch(`/api/leads/${lead.id}/notes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: value }),
        });
        if (res.ok) {
          setNoteDraft('');
          onSaved?.();
          if (notesEditorRef.current) notesEditorRef.current.innerHTML = '';
          setNotesSaved(true);
          setTimeout(() => setNotesSaved(false), 2000);
          loadNoteHistory();
        } else {
          const msg = await res.text();
          console.error('POST /api/leads/:id/notes failed', res.status, msg);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setIsSaving(false);
      }
    },
    [lead, noteDraft, onSaved, loadNoteHistory],
  );

  return {
    noteDraft,
    setNoteDraft,
    isSaving,
    notesSaved,
    noteHistory,
    loadNoteHistory,
    noteHistoryMenuEntryId,
    setNoteHistoryMenuEntryId,
    notesEditorRef,
    linkSavedRangeRef,
    notesSavedRangeRef,
    notesImageInputRef,
    showLinkModal,
    setShowLinkModal,
    linkModalText,
    setLinkModalText,
    linkModalUrl,
    setLinkModalUrl,
    showLinkHint,
    setShowLinkHint,
    imageSavedRangeRef,
    mentionPickerOpen,
    mentionQuery,
    mentionAnchorRect,
    pinnedNotesForLead,
    togglePinnedNoteHistory,
    deleteNoteHistoryEntry,
    handleNotesEditorInput,
    openMentionPickerAtCaret,
    closeMentionPicker,
    handleMentionSelect,
    handleNotesEditorMentionClick,
    handleNotesEditorKeyDown,
    saveNotes,
  };
}

export type UseLeadNotesResult = ReturnType<typeof useLeadNotes>;
